import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { ReactElement } from 'react';
import { BooleanField, BooleanInput, SelectArrayInput, TextField, TopToolbar } from 'react-admin';

import {
  GetB2bWithdrawalsQuery,
  GetB2bWithdrawalsResponse,
  getB2bWithdrawalsSortColumns,
} from '@Common/types/bo_external/withdrawals';

import { planCategoryChoices } from '@sh4res/bo-common/choices/plans';
import {
  b2bPaymentStatusChoices,
  b2bWithdrawalEarlyReasonChoices,
  b2bWithdrawalOrdersFlowChoices,
  b2bWithdrawalStatusChoices,
  b2bWithdrawalTypeChoices,
  bankAccountTypeChoices,
} from '@sh4res/bo-common/choices/withdrawals';
import { DatagridTyped, FilterButton, List } from '@sh4res/bo-common/components';
import { B2BWithdrawalBulkCreate } from '@sh4res/bo-common/components/B2bWithdrawalBulkCreate/B2BWithdrawalBulkCreate';
import { SelectColumnsButton } from '@sh4res/bo-common/components/SelectColumnsButton';
import {
  CurrencyField,
  IdField,
  SelectChipField,
  TextArrayInput,
  UTCDateField,
} from '@sh4res/bo-common/fields';
import { DateInput } from '@sh4res/bo-common/fields/DateInput';
import { useSelectedColumns } from '@sh4res/bo-common/hooks';
import { B2bWithdrawalsCreate } from '@sh4res/bo-common/resources/b2b/withdrawals';
import { RaSort } from '@sh4res/bo-common/utils';
import { createFilters } from '@sh4res/bo-common/utils/create_filters';

import { useCompanyDetails } from '@Hooks/useCompanyDetails';
import { taskStateChoices } from '@Resources/withdrawals/choices';

const withdrawalsFilters = createFilters<GetB2bWithdrawalsQuery>({
  id: <TextArrayInput asNumber source="" data-testid={'BoExternalWithdrawalsList_IdFilter'} />,
  employee_id: (
    <TextArrayInput asNumber source="" data-testid={'BoExternalWithdrawalsList_EmployeeIdFilter'} />
  ),
  contract_id: (
    <TextArrayInput asNumber source="" data-testid={'BoExternalWithdrawalsList_ContractIdFilter'} />
  ),
  investing_product: (
    <SelectArrayInput
      choices={planCategoryChoices}
      data-testid={'BoExternalWithdrawalsList_InvestingProductFilter'}
    />
  ),
  account_number: (
    <TextArrayInput source="" data-testid={'BoExternalWithdrawalsList_AccountNumberFilter'} />
  ),
  bank_country: (
    <TextArrayInput source="" data-testid={'BoExternalWithdrawalsList_BankCountryFilter'} />
  ),
  status: (
    <SelectArrayInput
      choices={b2bWithdrawalStatusChoices}
      data-testid={'BoExternalWithdrawalsList_StatusFilter'}
    />
  ),
  type: (
    <SelectArrayInput
      choices={b2bWithdrawalTypeChoices}
      data-testid={'BoExternalWithdrawalsList_TypeFilter'}
    />
  ),
  task_state: (
    <SelectArrayInput
      choices={taskStateChoices}
      data-testid={'BoExternalWithdrawalsList_TaskStateFilter'}
    />
  ),
  from_created_at: (
    <DateInput source="" data-testid={'BoExternalWithdrawalsList_FromCreatedAtFilter'} />
  ),
  to_created_at: (
    <DateInput source="" data-testid={'BoExternalWithdrawalsList_ToCreatedAtFilter'} />
  ),
  hide_campaign_withdrawals: (
    <BooleanInput
      source=""
      data-testid={'BoExternalWithdrawalsList_HideCampaignWithdrawalsFilter'}
    />
  ),
  correlation_id: (
    <TextArrayInput source="" data-testid={'BoExternalWithdrawalsList_CorrelationIdFilter'} />
  ),
});

export const withdrawalsListColumns: Record<keyof GetB2bWithdrawalsResponse[number], ReactElement> =
  {
    id: <IdField reference="withdrawals" data-testid={'BoExternalWithdrawalsList_IdColumn'} />,
    employee_id: (
      <IdField
        source=""
        reference="employees"
        data-testid={'BoExternalWithdrawalsList_EmployeeIdColumn'}
      />
    ),
    firstname: <TextField data-testid={'BoExternalWithdrawalsList_FirstnameColumn'} />,
    lastname: <TextField data-testid={'BoExternalWithdrawalsList_LastnameColumn'} />,
    contract_id: (
      <IdField
        source=""
        reference="contracts"
        data-testid={'BoExternalWithdrawalsList_ContractIdColumn'}
      />
    ),
    investing_product: (
      <SelectChipField
        choices={planCategoryChoices}
        data-testid={'BoExternalWithdrawalsList_InvestingProductColumn'}
      />
    ),
    orders_flow: (
      <SelectChipField
        choices={b2bWithdrawalOrdersFlowChoices}
        data-testid={'BoExternalWithdrawalsList_OrdersFlowColumn'}
      />
    ),
    is_planned_withdrawal: (
      <BooleanField data-testid={'BoExternalWithdrawalsList_IsPlannedWithdrawalColumn'} />
    ),
    account_type: (
      <SelectChipField
        choices={bankAccountTypeChoices}
        data-testid={'BoExternalWithdrawalsList_AccountTypeColumn'}
      />
    ),
    sort_code: <TextField data-testid={'BoExternalWithdrawalsList_SortCodeColumn'} />,
    account_number: <TextField data-testid={'BoExternalWithdrawalsList_AccountNumberColumn'} />,
    bank_country: <TextField data-testid={'BoExternalWithdrawalsList_BankCountryColumn'} />,
    status: (
      <SelectChipField
        choices={b2bWithdrawalStatusChoices}
        data-testid={'BoExternalWithdrawalsList_StatusColumn'}
      />
    ),
    type: (
      <SelectChipField
        choices={b2bWithdrawalTypeChoices}
        data-testid={'BoExternalWithdrawalsList_TypeColumn'}
      />
    ),
    gross_amount: (
      <CurrencyField
        currency="currency"
        data-testid={'BoExternalWithdrawalsList_GrossAmountColumn'}
      />
    ),
    net_amount: (
      <CurrencyField
        currency="currency"
        data-testid={'BoExternalWithdrawalsList_NetAmountColumn'}
      />
    ),
    tax_amount: (
      <CurrencyField
        currency="currency"
        data-testid={'BoExternalWithdrawalsList_TaxAmountColumn'}
      />
    ),
    initial_amount: (
      <CurrencyField
        currency="currency"
        data-testid={'BoExternalWithdrawalsList_InitialAmountColumn'}
      />
    ),
    currency: <TextField data-testid={'BoExternalWithdrawalsList_CurrencyColumn'} />,
    task_state: (
      <SelectChipField
        choices={taskStateChoices}
        data-testid={'BoExternalWithdrawalsList_TaskStateColumn'}
      />
    ),
    early_withdraw_reason: (
      <SelectChipField
        source=""
        choices={b2bWithdrawalEarlyReasonChoices}
        data-testid={'BoExternalWithdrawalsList_EarlyWithdrawReasonColumn'}
      />
    ),
    early_withdraw_event_date: (
      <UTCDateField data-testid={'BoExternalWithdrawalsList_EarlyWithdrawEventDateColumn'} />
    ),
    created_at: <UTCDateField showTime data-testid={'BoExternalWithdrawalsList_CreatedAtColumn'} />,
    updated_at: <UTCDateField showTime data-testid={'BoExternalWithdrawalsList_UpdatedAtColumn'} />,
    paid_at: <UTCDateField showTime data-testid={'BoExternalWithdrawalsList_PaidAtColumn'} />,
    payment_status: (
      <SelectChipField
        choices={b2bPaymentStatusChoices}
        data-testid={'BoExternalWithdrawalsList_PaymentStatusColumn'}
      />
    ),
    correlation_id: <TextField data-testid={'BoExternalWithdrawalsList_CorrelationIdColumn'} />,
  };

const defaultSort: RaSort<GetB2bWithdrawalsQuery> = { field: 'created_at', order: 'DESC' };

const defaultColumns: (keyof GetB2bWithdrawalsResponse[number])[] = [
  'id',
  'status',
  'employee_id',
  'type',
  'investing_product',
  'initial_amount',
  'created_at',
];

const WithdrawalsActionsItem = styled(Box)`
  margin: ${({ theme }) => theme.spacing(0, 0, 0, 0.5)};
`;

const WithdrawalsActions = () => {
  const companyDetails = useCompanyDetails();

  if (companyDetails === null) {
    return null;
  }

  return (
    <TopToolbar>
      <WithdrawalsActionsItem>
        <FilterButton data-testid={'BoExternalWithdrawalsActions_FilterButton'} />
      </WithdrawalsActionsItem>
      <WithdrawalsActionsItem>
        <SelectColumnsButton
          data-testid={'BoExternalWithdrawalsActions_SelectColumnsButton'}
          preference="withdrawals.list.columns"
          columns={withdrawalsListColumns}
          defaultColumns={defaultColumns}
          testId="BoExternalWithdrawalsList_SelectColumnsButton"
        />
      </WithdrawalsActionsItem>
      {companyDetails.is_international && (
        <WithdrawalsActionsItem>
          <B2BWithdrawalBulkCreate b2bCompanyId={companyDetails.id} />
          <B2bWithdrawalsCreate b2bCompanyId={companyDetails.id} />
        </WithdrawalsActionsItem>
      )}
    </TopToolbar>
  );
};

export function WithdrawalsList() {
  const columns = useSelectedColumns<GetB2bWithdrawalsResponse[number]>({
    preferences: 'withdrawals.list.columns',
    defaultColumns,
  });

  return (
    <List
      actions={<WithdrawalsActions />}
      filters={withdrawalsFilters}
      exporter={false}
      sort={defaultSort}
      empty={false}>
      <DatagridTyped<GetB2bWithdrawalsResponse[number]>
        rowClick={'show'}
        bulkActionButtons={false}
        displayedColumns={columns}
        columnRegistry={withdrawalsListColumns}
        sortableColumns={getB2bWithdrawalsSortColumns}
      />
    </List>
  );
}
